<template>
    <div id="manual-fax-component">
        <card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title">Send Fax</h1>
                </div>
            </template>
            <b-row align-v="baseline" class="mb-1">
                <b-col>
                    <b-form-group>
                        <h6>Fax Number</h6>
                        <b-input v-model="faxNumber" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-tabs pills card>
                <b-tab title="Upload Documents">
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending" />
                    <b-button variant="primary" :disabled="disableFaxButton" @click="sendFax" block size="lg" class="mt-4">
                        <strong>Send Fax</strong>
                    </b-button>
                </b-tab>
                <b-tab v-if="scanners.length" title="Scan Documents">
                    <label>Scanner:</label>
                    <b-select v-model="selectedScanner" :options="scanners" />

                    <b-overlay :show="isScanning" class="mt-4">
                        <b-button class="m-0" variant="primary" :disabled="isScanning || !validFax" @click="scan" block
                            size="lg">
                            <strong>Scan Documents and Send</strong>
                        </b-button>
                    </b-overlay>
                </b-tab>
            </b-tabs>
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator';
import Axios, { AxiosRequestConfig } from 'axios';
import Vue2Dropzone from 'vue2-dropzone';
import SearchComponent from "@/components/SearchComponent.vue";
import PatientDocumentsModal from '@/components/Patient/PatientDocumentsModal.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { LockHandler } from '@/mixins/LockHandler';
import IngestionImage from "@/components/IngestionImage.vue";
import Dropzone from "dropzone";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { DatePicker } from 'element-ui';

@Component({
    name: "ManualFaxComponent",
    components: {
        "VueDropzone": Vue2Dropzone,
        SearchComponent,
        PatientDocumentsModal,
        IngestionImage,
        Checkbox,
        [DatePicker.name]: DatePicker
    },
})
export default class ManualFaxComponent extends Mixins(LockHandler) {

    private scanners: Array<string> = [];
    private selectedScanner: string = "";
    private isScanning: boolean = false;
    @Ref("myVueDropzone") private dropzone!: Dropzone;
    private faxNumber: string = "";
    private workerMessageHandler: ((e: MessageEvent) => void) | null = null;

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.setupWorkerConnection();
        this.getAvailableScanners();
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
        if (this.hubTimeout) {
            clearTimeout(this.hubTimeout);
        }
        // Remove message handler
        if (this.workerMessageHandler && (this as any).$workerPort) {
            (this as any).$workerPort.removeEventListener('message', this.workerMessageHandler);
        }
    }

    setupWorkerConnection() {
        const workerPort = (this as any).$workerPort;
        if (!window.SharedWorker || !workerPort) {
            console.log("SharedWorker not available, will use HTTP fallback");
            return;
        }

        // Create message handler
        this.workerMessageHandler = (e: MessageEvent) => {
            const [messageType, data] = e.data;

            switch (messageType) {
                case "clientServiceScanners":
                    console.log("Received scanners from client service hub", data);
                    if (this.hubTimeout) {
                        clearTimeout(this.hubTimeout);
                        this.hubTimeout = null;
                    }
                    if (Array.isArray(data)) {
                        this.scanners = data.map((s: any) => s.name || s.id);
                        const previouslySelectedScanner = this.persistentScanner;
                        if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                            this.selectedScanner = previouslySelectedScanner;
                        }
                    }
                    break;
                case "clientServiceScanResponse":
                    console.log("Received scan response from client service hub", data);
                    this.isScanning = false;
                    if (this.hubTimeout) {
                        clearTimeout(this.hubTimeout);
                        this.hubTimeout = null;
                    }
                    if (data.error) {
                        this.$notification(NotificationOptions.error(data.error));
                    } else {
                        this.handleScanResponse(data);
                    }
                    break;
            }
        };

        // Add message handler
        workerPort.addEventListener('message', this.workerMessageHandler);
    }

    getAvailableScanners() {
        const workerPort = (this as any).$workerPort;
        // Try to use hub first if worker is available
        if (workerPort) {
            console.log("Requesting scanners from client service hub via worker");
            workerPort.postMessage(['clientServiceRefreshScanners', null]);

            // Set timeout to fallback to HTTP if hub doesn't respond
            this.hubTimeout = setTimeout(() => {
                console.log("Hub timeout, falling back to HTTP for scanners");
                this.getAvailableScannersFallback();
            }, 2000);
        } else {
            // Fallback to HTTP immediately if no worker
            this.getAvailableScannersFallback();
        }
    }

    getAvailableScannersFallback() {
        Axios.get('http://localhost:7274/scanner').then((result) => {
            this.scanners = result.data.map((s: any) => s.name);
            const previouslySelectedScanner = this.persistentScanner;
            if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                this.selectedScanner = previouslySelectedScanner;
            }
            if (this.hubTimeout) {
                clearTimeout(this.hubTimeout);
                this.hubTimeout = null;
            }
        }).catch((err: any) => {
            console.warn(err);
        });
    }

    get persistentScanner(): any {
        return localStorage.getItem('ingestion_selected_scanner');
    }

    @Watch('selectedScanner')
    selectedScannerChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_selected_scanner', val1);
    }

    get validFax(): boolean {
        return this.faxNumber.length >= 10;
    }

    // dropzone
    // properties
    get dropzoneOptions(): any {
        return {
            url: `${Axios.defaults.baseURL}/image?bypassIngestion=true`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: 'images',
            autoProcessQueue: false,
        };
    }

    // events
    dropzoneSending(_file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    get disableFaxButton(): boolean {
        return !this.validFax;
    }

    sendFax() {
        this.dropzone.processQueue();
    }

    async dropzoneUploadSuccess(file: any, response: number[]) {
        try {
            await Axios.post("api/Fax/Send", {
                imageId: response[0],
                faxNumber: this.faxNumber,
            });
            this.$notification(NotificationOptions.success("Fax sent successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error("Failed to send fax. Please ensure the fax number is in the correct format: +15551234567 or 15551234567"));
        }
        this.dropzone.removeFile(file);
    }

    scan() {
        if (this.selectedScanner == "") {
            this.$notification(NotificationOptions.error("Please select a scanner."));
            return;
        }
        this.isScanning = true;

        const workerPort = (this as any).$workerPort;
        // Try to use hub first if worker is available
        if (workerPort) {
            console.log("Requesting scan from client service hub via worker");
            workerPort.postMessage(['clientServiceScan', this.selectedScanner]);

            // Set timeout to fallback to HTTP if hub doesn't respond
            this.hubTimeout = setTimeout(() => {
                console.log("Hub timeout, falling back to HTTP for scan");
                this.scanFallback();
            }, 5000); // Longer timeout for scanning since it takes time
        } else {
            // Fallback to HTTP immediately if no worker
            this.scanFallback();
        }
    }

    scanFallback() {
        const scanConfig: AxiosRequestConfig = {
            params: { scannerId: this.selectedScanner },
            responseType: 'blob',
        };
        Axios.get('http://localhost:7274/scanner/scan', scanConfig)
            .then(result => {
                this.handleScanResponse(result.data);
            })
            .catch(error => {
                console.log(error);
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isScanning = false;
                if (this.hubTimeout) {
                    clearTimeout(this.hubTimeout);
                    this.hubTimeout = null;
                }
            });
    }

    handleScanResponse(data: any) {
        // Convert response to blob if needed
        let blob;
        if (data instanceof Blob) {
            blob = data;
        } else if (data instanceof ArrayBuffer) {
            blob = new Blob([data], { type: 'application/pdf' });
        } else if (Array.isArray(data)) {
            blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
        } else if (typeof data === 'string') {
            // Handle base64 string
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: 'application/pdf' });
        } else {
            console.error("Unexpected scan response format", data);
            this.$notification(NotificationOptions.error("Unexpected scan response format"));
            return;
        }

        const formData = new FormData();
        formData.append("images", blob, "scan.pdf");
        const imageConfig = {
            headers: { "Content-Type": "multipart/form-data" },
            params: { bypassIngestion: true },
        };
        Axios.post('/image', formData, imageConfig)
            .then(response => {
                Axios.post("api/Fax/Send", {
                    imageId: response.data[0],
                    faxNumber: this.faxNumber,
                })
                    .then(_ => {
                        this.$notification(NotificationOptions.success("Fax sent successfully."));
                    })
                    .catch(err => {
                        this.$notification(NotificationOptions.error("Failed to send fax. Please ensure the fax number is in the correct format: +15551234567 or 15551234567"));
                    });
            })
            .catch(error => {
                console.log(error);
                this.$notification(NotificationOptions.error(error));
            });
    }

}
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
::v-deep table#escript-queue .flip-list-move {
    transition: transform .5s;
}

/*noinspection CssUnusedSymbol*/

::v-deep .selected-col {
    width: 2rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .button-col {
    width: 10rem;
    padding-top: 0;
    padding-bottom: 0;
}

/*noinspection CssUnusedSymbol*/
::v-deep .date-col {
    width: 10rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .type-col {
    width: 11rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .ingestion-item {
    min-height: 20rem;
    max-height: 40rem;
}

::v-deep(.locked),
::v-deep(.completed) {
    width: 200px;
}

::v-deep(.vue-dropzone) {
    border: 2px dashed red;
}

::v-deep(.dz-clickable) {
    border: 2px dashed green;
}
</style>
